import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Button } from '../../components/ui/Button';
import { Video, Users, BookOpen, Headphones, Calendar, MessageCircle } from 'lucide-react';
export function WaysToLearn() {
  const learningPaths = [{
    icon: <Video className="h-12 w-12" data-id="element-1592" />,
    title: 'Self-Paced Video Courses',
    description: 'Learn on your own schedule with our library of recorded workshops, lectures, and guided exercises.',
    features: ['Lifetime access', 'Downloadable resources', 'Discussion forums', 'Certificate of completion'],
    bestFor: 'People with unpredictable schedules, those who prefer to learn alone, international learners across time zones',
    price: '$50-150 per course',
    color: 'cyan'
  }, {
    icon: <Users className="h-12 w-12" data-id="element-1593" />,
    title: 'Cohort-Based Learning',
    description: 'Join a small group of peers for deep dives, live discussions, and collective processing over 4-8 weeks.',
    features: ['Live weekly sessions', 'Peer accountability', 'Facilitated discussions', 'Community Slack access'],
    bestFor: 'People who thrive in community, those seeking accountability, anyone wanting to build lasting connections',
    price: '$150-600 sliding scale',
    color: 'magenta'
  }, {
    icon: <BookOpen className="h-12 w-12" data-id="element-1594" />,
    title: 'Reading Groups',
    description: 'Facilitated discussions of key texts in fat liberation, disability justice, and anti-oppression theory.',
    features: ['Bi-weekly meetings', 'Curated reading lists', 'Expert facilitators', 'Book club vibes'],
    bestFor: 'Book lovers, theory nerds, people who process through discussion, those building a personal library',
    price: '$100-300 sliding scale',
    color: 'orange'
  }, {
    icon: <Headphones className="h-12 w-12" data-id="element-1595" />,
    title: 'Audio Learning Library',
    description: 'Podcast-style lessons, interviews, and reflections you can listen to while commuting, cooking, or resting.',
    features: ['50+ episodes', 'Transcripts included', 'Downloadable', 'New content monthly'],
    bestFor: 'Auditory learners, multitaskers, people with screen fatigue, those who learn while moving',
    price: '$15/month or $150/year',
    color: 'lime'
  }, {
    icon: <Calendar className="h-12 w-12" data-id="element-1596" />,
    title: 'Workshops & Events',
    description: 'One-time intensive workshops on specific topics, from medical advocacy to workplace organizing.',
    features: ['2-4 hour sessions', 'Practical tools', 'Q&A with experts', 'Recordings available'],
    bestFor: 'People wanting to dive deep on one topic, those testing out our teaching style, busy schedules',
    price: '$25-150 sliding scale',
    color: 'cyan'
  }, {
    icon: <MessageCircle className="h-12 w-12" data-id="element-1597" />,
    title: 'Community Membership',
    description: 'Access to our online community, monthly gatherings, and all self-paced content for one annual fee.',
    features: ['All video courses', 'Monthly community calls', 'Private forums', 'Resource library'],
    bestFor: 'People committed to long-term learning, those seeking ongoing community, anyone wanting full access',
    price: '$300-900/year sliding scale',
    color: 'magenta'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1598">
      <HeroSection title="Ways to Learn With Us" subtitle="Different paths for different needs. All rooted in justice." tag="Learning & Growth" bgColor="cyan" textColor="navy" data-id="element-1599" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center" data-id="element-1600">
        <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1601">
          We know people learn differently. Some thrive in community, others
          need solitude. Some love reading, others prefer listening. We've
          designed multiple pathways so you can find what works for{' '}
          <strong className="text-brand-cyan" data-id="element-1602">
            your brain, your schedule, and your life
          </strong>
          .
        </p>
      </section>

      {/* Learning Paths */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1603">
        <div className="space-y-12" data-id="element-1604">
          {learningPaths.map((path, index) => <div key={index} className={`border-2 border-brand-black bg-white ${index % 2 === 0 ? 'md:ml-0 md:mr-12' : 'md:ml-12 md:mr-0'}`} data-id="element-1605">
              <div className="grid grid-cols-1 md:grid-cols-3" data-id="element-1606">
                {/* Icon & Title Column */}
                <div className={`p-8 border-b-2 md:border-b-0 md:border-r-2 border-brand-black bg-brand-${path.color}/5 flex flex-col justify-center`} data-id="element-1607">
                  <div className={`text-brand-${path.color} mb-4`} data-id="element-1608">
                    {path.icon}
                  </div>
                  <h3 className="text-2xl font-black uppercase text-brand-black mb-3" data-id="element-1609">
                    {path.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1610">
                    {path.description}
                  </p>
                  <p className="text-lg font-black text-brand-cyan" data-id="element-1611">
                    {path.price}
                  </p>
                </div>

                {/* Details Column */}
                <div className="p-8 md:col-span-2" data-id="element-1612">
                  <div className="mb-6" data-id="element-1613">
                    <h4 className="text-sm font-bold uppercase text-gray-500 mb-3" data-id="element-1614">
                      What's Included:
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1615">
                      {path.features.map((feature, i) => <div key={i} className="flex items-center" data-id="element-1616">
                          <span className="text-brand-cyan mr-2" data-id="element-1617">✓</span>
                          <span className="text-sm" data-id="element-1618">{feature}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-gray-50 border-l-4 border-brand-cyan" data-id="element-1619">
                    <h4 className="text-sm font-bold uppercase text-gray-500 mb-2" data-id="element-1620">
                      Best For:
                    </h4>
                    <p className="text-sm text-gray-700" data-id="element-1621">{path.bestFor}</p>
                  </div>

                  <Button color="cyan" variant="outline" size="sm" withArrow data-id="element-1622">
                    Explore This Path
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white" data-id="element-1623">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1624">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black text-center" data-id="element-1625">
            Quick Comparison
          </h2>

          <div className="overflow-x-auto" data-id="element-1626">
            <table className="w-full border-2 border-brand-black" data-id="element-1627">
              <thead data-id="element-1628">
                <tr className="bg-brand-cyan text-brand-navy" data-id="element-1629">
                  <th className="p-4 text-left font-black uppercase text-sm border-r-2 border-brand-black" data-id="element-1630">
                    Learning Path
                  </th>
                  <th className="p-4 text-center font-black uppercase text-sm border-r-2 border-brand-black" data-id="element-1631">
                    Time Commitment
                  </th>
                  <th className="p-4 text-center font-black uppercase text-sm border-r-2 border-brand-black" data-id="element-1632">
                    Community
                  </th>
                  <th className="p-4 text-center font-black uppercase text-sm" data-id="element-1633">
                    Price Range
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1634">
                <tr className="border-t-2 border-brand-black" data-id="element-1635">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1636">
                    Self-Paced Video
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1637">
                    Flexible
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1638">
                    Optional forums
                  </td>
                  <td className="p-4 text-center" data-id="element-1639">$50-150</td>
                </tr>
                <tr className="border-t-2 border-brand-black bg-gray-50" data-id="element-1640">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1641">
                    Cohort-Based
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1642">
                    4-8 weeks
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1643">
                    High engagement
                  </td>
                  <td className="p-4 text-center" data-id="element-1644">$150-600</td>
                </tr>
                <tr className="border-t-2 border-brand-black" data-id="element-1645">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1646">
                    Reading Groups
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1647">
                    6-8 weeks
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1648">
                    Discussion-based
                  </td>
                  <td className="p-4 text-center" data-id="element-1649">$100-300</td>
                </tr>
                <tr className="border-t-2 border-brand-black bg-gray-50" data-id="element-1650">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1651">
                    Audio Library
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1652">
                    Ongoing
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1653">
                    Minimal
                  </td>
                  <td className="p-4 text-center" data-id="element-1654">$15/month</td>
                </tr>
                <tr className="border-t-2 border-brand-black" data-id="element-1655">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1656">
                    Workshops
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1657">
                    2-4 hours
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1658">
                    Live Q&A
                  </td>
                  <td className="p-4 text-center" data-id="element-1659">$25-150</td>
                </tr>
                <tr className="border-t-2 border-brand-black bg-gray-50" data-id="element-1660">
                  <td className="p-4 font-bold border-r-2 border-brand-black" data-id="element-1661">
                    Membership
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1662">
                    Year-round
                  </td>
                  <td className="p-4 text-center border-r-2 border-brand-black" data-id="element-1663">
                    Full access
                  </td>
                  <td className="p-4 text-center" data-id="element-1664">$300-900/year</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-brand-cyan/10" data-id="element-1665">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1666">
          <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1667">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-1668">
            Book a free 15-minute call with our team. We'll help you find the
            learning path that fits your goals, schedule, and budget.
          </p>
          <Button color="cyan" size="lg" data-id="element-1669">
            Schedule a Call
          </Button>
        </div>
      </section>
    </div>;
}